import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import useCampaign from '../hooks/useCampaign.js';

export default function CampaignPage() {
  const { campaign } = useCampaign();

  const { data: stats, isLoading } = useQuery({
    queryKey: ['campaignStats', campaign?.id],
    queryFn: () => api.getCampaignStats(campaign.id),
    enabled: !!campaign?.id,
  });

  const { data: prospectsData } = useQuery({
    queryKey: ['prospects', campaign?.id],
    queryFn: () => api.listProspects(campaign.id),
    enabled: !!campaign?.id,
  });

  if (!campaign) {
    return (
      <div className="p-8 text-gray-600">No active campaign.</div>
    );
  }

  if (isLoading || !stats) {
    return <div className="p-8 text-gray-600">Loading...</div>;
  }

  const progressPct = Math.min(
    100,
    (stats.totals.total_raised / (campaign.goal_amount || 1)) * 100
  );

  const prospects = prospectsData?.prospects || [];

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-6xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{campaign.name}</h1>
          <p className="text-gray-600 text-sm">{campaign.description}</p>
          {campaign.event_date && (
            <p className="text-sm text-gray-500 mt-1">
              Event date: {new Date(campaign.event_date).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Goal progress */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-end justify-between mb-2">
            <div>
              <div className="text-xs text-gray-500 uppercase font-semibold">Raised</div>
              <div className="text-3xl font-bold text-ccf-accent">
                ${stats.totals.total_raised.toLocaleString()}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500 uppercase font-semibold">Goal</div>
              <div className="text-xl font-semibold text-gray-700">
                ${(campaign.goal_amount || 0).toLocaleString()}
              </div>
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-ccf-accent h-3 rounded-full transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="mt-2 text-xs text-gray-500">
            {progressPct.toFixed(0)}% of goal · Pipeline: ${stats.totals.pipeline.toLocaleString()}
          </div>
        </div>

        {/* Pipeline + Contribution types */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold text-gray-900 mb-3">Prospect Pipeline</h3>
            {stats.status_pipeline.length === 0 ? (
              <div className="text-sm text-gray-500">No prospects yet</div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-xs text-gray-500 uppercase text-left">
                    <th className="pb-2">Status</th>
                    <th className="pb-2 text-right">Count</th>
                    <th className="pb-2 text-right">Committed</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.status_pipeline.map((s) => (
                    <tr key={s.status} className="border-b border-gray-100">
                      <td className="py-2">
                        <StatusPill status={s.status} />
                      </td>
                      <td className="text-right">{s.count}</td>
                      <td className="text-right text-ccf-accent">
                        ${s.total_committed.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="font-semibold text-gray-900 mb-3">By Contribution Type</h3>
            {stats.by_type.length === 0 ? (
              <div className="text-sm text-gray-500">No contributions yet</div>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {stats.by_type.map((t) => (
                    <tr key={t.contribution_type} className="border-b border-gray-100">
                      <td className="py-2 capitalize">{t.contribution_type.replace('_', ' ')}</td>
                      <td className="text-right text-gray-500">{t.count}</td>
                      <td className="text-right text-ccf-accent font-semibold">
                        ${t.total.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* By board member */}
        {stats.by_assignee.length > 0 && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h3 className="font-semibold text-gray-900 mb-3">Board Member Activity</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs text-gray-500 uppercase text-left">
                  <th className="pb-2">Member</th>
                  <th className="pb-2 text-right">Prospects</th>
                  <th className="pb-2 text-right">Committed</th>
                  <th className="pb-2 text-right">Raised</th>
                </tr>
              </thead>
              <tbody>
                {stats.by_assignee.map((a) => (
                  <tr key={a.assigned_to} className="border-b border-gray-100">
                    <td className="py-2 font-medium">{a.assigned_to}</td>
                    <td className="text-right">{a.prospects}</td>
                    <td className="text-right">{a.committed}</td>
                    <td className="text-right text-ccf-accent font-semibold">
                      ${a.total_raised.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Prospects table */}
        {prospects.length > 0 && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">All Prospects</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase">Business</th>
                  <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase">Status</th>
                  <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase">Type</th>
                  <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase text-right">Ask</th>
                  <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase text-right">Committed</th>
                  <th className="px-4 py-2 text-xs font-semibold text-gray-700 uppercase">Assigned</th>
                </tr>
              </thead>
              <tbody>
                {prospects.map((p) => (
                  <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <Link to={`/business/${p.business_id}`} className="text-ccf-primary hover:underline">
                        {p.business_name}
                      </Link>
                      <div className="text-xs text-gray-500">{p.address}</div>
                    </td>
                    <td className="px-4 py-2"><StatusPill status={p.status} /></td>
                    <td className="px-4 py-2 text-xs capitalize">{p.contribution_type?.replace('_', ' ') || '—'}</td>
                    <td className="px-4 py-2 text-xs text-right">
                      {p.ask_amount ? `$${p.ask_amount.toLocaleString()}` : '—'}
                    </td>
                    <td className="px-4 py-2 text-xs text-right text-ccf-accent font-semibold">
                      {p.committed_amount ? `$${p.committed_amount.toLocaleString()}` : '—'}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600">{p.assigned_to || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const colors = {
    identified: 'bg-gray-100 text-gray-700',
    contacted: 'bg-blue-100 text-blue-700',
    interested: 'bg-purple-100 text-purple-700',
    committed: 'bg-emerald-100 text-emerald-700',
    declined: 'bg-red-100 text-red-700',
    no_response: 'bg-yellow-100 text-yellow-700',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded ${colors[status] || 'bg-gray-100'}`}>
      {status}
    </span>
  );
}
